/**
 * useGamePhase Hook
 *
 * Phase management hook. Provides:
 * - Current phase tracking
 * - Phase transition validation
 * - Phase-specific helpers
 *
 * Used by:
 * - GamePage (phase routing)
 * - Phase components (knowing current phase)
 * - Other hooks (phase-aware logic)
 *
 * Integration:
 * - Wraps useGameStateContext to provide phase-specific functionality
 * - Uses gameHelpers for phase validation
 *
 * Dependencies: types/game.types.ts, GameStateContext, gameHelpers.ts
 */

import { useMemo } from 'react';
import { useGameStateContext } from '../context/useGameStateContext';
import { isValidPhaseTransition, phaseToNumber } from '../utils/gameHelpers';
import type { GamePhase } from '../types/game.types';

export interface UseGamePhaseReturn {
    // Current phase
    phase: GamePhase;
    phaseNumber: number;

    // Phase checks
    isLobby: boolean;
    isIslandPlacement: boolean;
    isMonsterPlacement: boolean;
    isActive: boolean;
    isEnded: boolean;

    // Phase transition
    canTransitionTo: (targetPhase: GamePhase) => boolean;

    // Helper functions
    setPhase: (phase: GamePhase) => void;
}

/**
 * useGamePhase hook
 * @returns Phase state and management functions
 */
export const useGamePhase = (): UseGamePhaseReturn => {
    const { phase, setPhase: setPhaseState } = useGameStateContext();

    // Derived phase state
    const phaseNumber = useMemo(() => phaseToNumber(phase), [phase]);

    const isLobby = phase === 'LOBBY';
    const isIslandPlacement = phase === 'ISLAND_PLACEMENT';
    const isMonsterPlacement = phase === 'MONSTER_PLACEMENT';
    const isActive = phase === 'ACTIVE';
    const isEnded = phase === 'ENDED';

    // Phase transition validation
    const canTransitionTo = useMemo(() => {
        return (targetPhase: GamePhase): boolean => {
            return isValidPhaseTransition(phase, targetPhase);
        };
    }, [phase]);

    return {
        // Current phase
        phase,
        phaseNumber,

        // Phase checks
        isLobby,
        isIslandPlacement,
        isMonsterPlacement,
        isActive,
        isEnded,

        // Phase transition
        canTransitionTo,

        // Helper functions
        setPhase: setPhaseState,
    };
};

export default useGamePhase;
